/**
 * Paints the wordmark.
 *
 * A canvas, not 47×7 spans. The mark is 329 lit pixels; as DOM that is 329
 * nodes on a screen the player reaches while the run is being torn down, and
 * every one of them a layout box the compositor has to think about. One canvas
 * at native size, upscaled by `image-rendering: pixelated`, is one node and the
 * scaling is exact at any factor.
 *
 * Also not vector art, deliberately. The audit greps this directory for inline
 * markup and path data and expects no hits; this file is not going to be what
 * changes that, and it does not name the pattern either, because a comment that
 * quotes the search term is a hit.
 *
 * Two colours, taken from `core/palette` rather than written out here, so the
 * mark wears the same two phosphors as everything else on the glass. `hex`
 * rather than `phosphorVar`: a canvas `fillStyle` cannot resolve `var(--ph-…)`,
 * it needs the value.
 */

import { hex } from '@/core/palette';
import { LOGO_HEIGHT, LOGO_ROWS, LOGO_WIDTH } from '@/assets/logo';
import { el, installUiTheme } from './Icon';

/** How many CSS pixels one logo pixel gets. */
const SCALE = { sm: 3, md: 4, lg: 6 } as const;

export type LogoSize = keyof typeof SCALE;

/**
 * A node carrying the mark, ready to append.
 *
 * Returns a plain element on any platform without a 2D context — a headless
 * runner, a browser that has run out of them — rather than throwing. A missing
 * logo is a cosmetic loss; an exception here takes the run-over sheet with it.
 */
export function logoNode(size: LogoSize = 'md'): HTMLElement {
  installUiTheme();
  const scale = SCALE[size];
  const box = el('div', `logo logo--${size}`);
  box.setAttribute('role', 'img');
  box.setAttribute('aria-label', 'PIXEL//TD');

  const canvas = document.createElement('canvas');
  canvas.className = 'logo__px';
  canvas.width = LOGO_WIDTH;
  canvas.height = LOGO_HEIGHT;
  canvas.style.width = `${LOGO_WIDTH * scale}px`;
  canvas.style.height = `${LOGO_HEIGHT * scale}px`;

  const ctx = canvas.getContext('2d');
  if (ctx) {
    const body = hex('white');
    const accent = hex('yellow');
    for (let y = 0; y < LOGO_HEIGHT; y++) {
      const row = LOGO_ROWS[y];
      for (let x = 0; x < row.length; x++) {
        const ch = row[x];
        if (ch === '.') continue;
        ctx.fillStyle = ch === '+' ? accent : body;
        ctx.fillRect(x, y, 1, 1);
      }
    }
  }

  box.append(canvas);
  return box;
}
